/**
 * What a `call.end` frame does to what this device is showing (A15).
 *
 * The frame carries `by` (the userId that declined or ended) and `reason`, so a client no longer
 * has to infer "was that me?" from `remoteParticipants == 0`. The rules are the ones the native
 * client pins in `apps/android-native/.../call/CallRules.kt` `endEffect`:
 *
 *  - `ended` — the call row is closed and the SFU room deleted. The call is over, whoever did it.
 *  - `declined` — one member refused the ring. The call row stays live (docs/CONTRACT.md §4) so a
 *    group call carries on without them; the only case that ends anything here is a caller left
 *    with the sole member who could have answered having said no.
 *  - our own frame comes back to us on every decline and end; the local action already applied it,
 *    and re-applying it would tear down the call of whoever else is still in it.
 *
 * Pure on purpose: two call sites (the socket and the tests) must not decide it differently.
 */
import type { ConnectionState } from 'livekit-client'
import { isolateLtr } from './bidi'
import type { MessageKey } from './i18n'

export type CallEndEffect = {
  /** Clear `ChatView.hasActiveCall` — the call row is closed. */
  closeChatCall: boolean;
  /** Close the ring this device is showing. */
  dismissRing: boolean;
  /** Tear down the call this device is in, or handed to the native host. */
  leaveCall: boolean;
  /** The userId whose decline left nobody to answer, so the caller is told about it by name. */
  declinedBy: string | null;
};

const NOTHING: CallEndEffect = {
  closeChatCall: false,
  dismissRing: false,
  leaveCall: false,
  declinedBy: null,
};

export type CallEndInput = {
  /** `WsCallEnd.by`. */
  by: string;
  /** `WsCallEnd.reason`. */
  reason: 'declined' | 'ended';
  meId: string | null;
  /** This device is in (or waiting in) the call the frame names: active or handed to the host. */
  inCall: boolean;
  /** This device is showing the ring for it. */
  ringing: boolean;
  /** The chat's members other than the viewer. */
  otherMemberIds: string[];
};

export function callEndEffect(input: CallEndInput): CallEndEffect {
  if (input.reason === 'ended') {
    return {
      closeChatCall: true,
      dismissRing: input.ringing,
      leaveCall: input.inCall,
      declinedBy: null,
    };
  }

  // A decline. Our own echo, a call this device only rings for (a decline is never about the
  // person who has not answered), and a call this device is not in all leave the screen alone.
  if (input.by === input.meId || input.ringing || !input.inCall) return NOTHING;

  // One other member declined: nobody is left who could answer, so the caller stops waiting. With
  // more than one other member the call carries on and this device stays in it.
  const onlyOther = input.otherMemberIds.length === 1 ? input.otherMemberIds[0] : undefined;
  if (onlyOther !== input.by) return NOTHING;
  return { closeChatCall: false, dismissRing: false, leaveCall: true, declinedBy: input.by };
}

/* ------------------------------------------------------------------- W7: the sibling's answer */

/**
 * A `call.dismiss` frame (`WsCallDismiss` in protocol.ts): one of this user's devices answered or
 * declined the call, so their **other** devices must stop ringing it.
 */
export type RingDismissal = {
  /** The call that was answered or declined elsewhere. */
  callId: string;
  /**
   * Which of the two happened. The ring's own teardown does not read it — the ring goes either way,
   * the same way it goes when the user answers or declines on this device — but the page left under
   * a push-rung ring words itself from it (`IncomingCallRoute`).
   */
  reason: 'answered' | 'declined';
};

/** Call ids this device is done ringing for, and why (`callStore.dismissedRings`). */
export type DismissedRings = Record<string, RingDismissal['reason']>;

/** What these rules read on the store's `IncomingCall`: which call this device is ringing for. */
export type RingingCall = { call: { id: string } };

/**
 * Whether `call.dismiss` takes down the ring **this** device is showing.
 *
 * The frame reaches every socket the user owns, the device that just answered or declined included,
 * and a device may be ringing a different call — or already be done with this one: only the ring for
 * `callId` itself comes down, and everything else is a no-op.
 */
export function shouldDismissRing(ringing: RingingCall | null, callId: string): boolean {
  return ringing?.call.id === callId;
}

/* ------------------------------------------------ W12/C2: the connection line, and call audio */

/**
 * Whether LiveKit is in the middle of getting the connection back. `Reconnecting` and
 * `SignalReconnecting` start an attempt; `Reconnected` ends one; `Disconnected` is the attempt
 * having *failed* — the room is gone, which is not "trying" any more.
 */
export type ReconnectPhase = 'steady' | 'trying';

/**
 * What the call's own status line says, from the room's state and the reconnection phase. The
 * SDK's enum never reaches the screen: `signalReconnecting` is not a word, and the line is the one
 * place a user can read what happened to their call.
 *
 * The states are spelled out here rather than imported as values, so this module stays free of the
 * SDK: every `ConnectionState` member is a key of the table, which `tsc` enforces — an SDK that
 * grows a state cannot be forgotten, because `Record<ConnectionState, …>` stops compiling.
 *
 * Two decisions worth keeping (both pinned in the tests):
 *
 *  - **A live attempt outranks the state's word.** The events are what know an attempt has started;
 *    a state read that has not caught up yet must not print "Connected" over it. The one exception
 *    is `disconnected` — the room is gone, so nothing is being tried on this screen any more.
 *  - **`signalReconnecting` is the same word as `reconnecting`.** LiveKit's own toast hides the
 *    signal-only blip (its media connection is usually still up), but this line is the honest
 *    readout of the link, and one word for one phenomenon beats two words for a distinction the
 *    reader cannot act on differently.
 */
const CONNECTION_KEYS: Record<ConnectionState, MessageKey> = {
  connected: 'call.connected',
  connecting: 'call.connecting',
  reconnecting: 'call.reconnecting',
  signalReconnecting: 'call.reconnecting',
  disconnected: 'call.disconnected',
};

export function connectionKey(state: ConnectionState, phase: ReconnectPhase): MessageKey {
  if (phase === 'trying' && state !== 'disconnected') return 'call.reconnecting'
  // The `??` is for a state a future SDK adds: `tsc` stops compiling at the table above before that
  // happens, and if one ever reaches here it reads as "not in the room yet" rather than as itself.
  return CONNECTION_KEYS[state] ?? 'call.connecting'
}

/** The slider's step. 5 % is twenty stops: one arrow press is a nudge you can hear. */
export const VOLUME_STEP = 5;

/**
 * The volume LiveKit is playing a participant at, as the control's 0–100. `undefined` is a
 * participant whose volume has never been set — that is full volume, which is what the SDK plays.
 * Clamped, because the slider is the only writer here and it can only say 0–100.
 */
export function percentFromVolume(volume: number | undefined): number {
  if (volume === undefined || !Number.isFinite(volume)) return 100;
  return Math.round(Math.min(1, Math.max(0, volume)) * 100);
}

/** What `participant.setVolume()` wants: the slider's 0–100 as the SDK's 0–1, clamped both ways. */
export function volumeFromPercent(percent: number): number {
  if (!Number.isFinite(percent)) return 1;
  return Math.min(1, Math.max(0, percent / 100));
}

/**
 * `80%` — a percent is a formatted number, so it is one closed unit (docs/DESIGN.md §7.9 rule 9):
 * the value is glued into a row whose chrome may be Arabic, and an unclosed `80%` can lay out from
 * the wrong end of the line.
 */
export function volumeLabel(percent: number): string {
  return isolateLtr(`${Math.round(Math.min(100, Math.max(0, percent)))}%`);
}

/** The four outputs the design names — the phone's own `CallRoute`, in the web's words. */
export type OutputKind = 'speaker' | 'earpiece' | 'headphones' | 'bluetooth';

/**
 * What an audio output *is*, read off the platform's own label, or `null` when the label is not
 * something we can read with confidence.
 *
 * The label belongs to the platform — "Bluetooth Headphones (Jabra Elite 65t)", "MacBook Pro
 * Speakers", "Default" — and it is in the platform's language. The control words the *kind* from
 * the dictionary (docs/DESIGN.md §14) and keeps the label as the row's own second line, so the
 * sentence is translated while the device still says what it is called. Deliberately a small truth
 * table and not a brand list: a label we cannot read shows itself, which is honest, where a guess
 * would be a device named wrong. Bluetooth wins over the shape it comes in ("Bluetooth headset" is
 * a Bluetooth output, and that is the difference the picker exists to show).
 */
export function outputKind(label: string): OutputKind | null {
  const text = label.trim().toLowerCase();
  if (text === '' || text.startsWith('default')) return null;
  if (text.includes('bluetooth') || text.includes('بلوتوث')) return 'bluetooth';
  if (
    /(headphone|headset|earphone|earbud|airpod|\bbuds\b)/.test(text) ||
    text.includes('سماعات') ||
    text.includes('هيدفون')
  ) {
    return 'headphones';
  }
  if (text.includes('earpiece') || text.includes('handset') || text.includes('سماعة الأذن')) {
    return 'earpiece';
  }
  if (text.includes('speaker') || text.includes('loudspeaker') || text.includes('مكبر الصوت')) {
    return 'speaker';
  }
  return null;
}

/**
 * The output row that carries the mark, from what the room says it is on and the platform's list.
 *
 * LiveKit answers `default` — its name for "whatever the platform picks" — until something is
 * switched, and the browsers differ in how they spell that device: Chrome lists an entry whose id
 * *is* `default`, Firefox lists the platform's default first, with a real id. So `default`/empty
 * marks the first row, which is that device on both; a real id marks itself; and an id that is no
 * longer in the list (a headset unplugged) marks nothing rather than the wrong device — the room's
 * own device change will move the mark on the next event.
 */
export function activeOutputId(active: string, outputs: readonly { deviceId: string }[]): string {
  if (active !== '' && active !== 'default') return active;
  return outputs[0]?.deviceId ?? '';
}
